/**
 * Text for the information card: the honesty-ladder subset this client ships.
 *
 * docs/03-DESIGN.md §9.4 defines four rungs (curated, the concept's own
 * Wikipedia/Wikidata text, inherited ancestor text, the system paragraph).
 * Rungs 2 and 3 need a network fetch against Wikidata/Wikipedia and are not
 * part of this MVP; this rule implements the two rungs the shipped content
 * supports:
 *
 *   1. **Curated** — one of Human Atlas's nine hand-written explanations,
 *      matched to the selected group's name case-insensitively (or with a
 *      leading "Left "/"Right " removed, so a lateralised part still reaches
 *      the medial explanation once one exists for it).
 *   4. **System overview** — the group's system paragraph, always available
 *      once the group resolves to a system the content carries.
 *
 * Every card names its rung and licence, so nothing curated-sounding is ever
 * silently generic (the design's own honesty rule, made mechanical). Pure and
 * engine-free like every rule here: it takes data in and returns data out.
 */
import rawHumanAtlasText from "../content/human-atlas-text.json";
import type { Catalog, Group } from "../model.js";

export interface HumanAtlasSystemText {
  readonly id: string;
  readonly name: string;
  readonly description: string;
}

export interface HumanAtlasText {
  readonly source: string;
  readonly systems: readonly HumanAtlasSystemText[];
  /** Keyed by the concept's common name, lowercase (e.g. "heart", "urinary bladder"). */
  readonly explanations: Readonly<Record<string, string>>;
}

/**
 * The shipped Human Atlas text (MIT code, CC BY 4.0 anatomy). Cast once here:
 * a JSON import's inferred type has no index signature, so every other file
 * that wants it imports this typed constant rather than the raw JSON.
 */
export const HUMAN_ATLAS_TEXT: HumanAtlasText = rawHumanAtlasText as HumanAtlasText;

export type InfoRung = 1 | 4;

export interface InfoText {
  rung: InfoRung;
  text: string;
  /** States the rung and licence, so the card never presents text as more specific than it is. */
  source: string;
}

const CURATED_SOURCE = "From Human Atlas (MIT) · anatomy BodyParts3D, CC BY 4.0";
const SYSTEM_SOURCE = "System overview · structure identified from source anatomy";

/** A sensible cap for a card meant to be read at a glance, not a document. */
const MAX_CARD_CHARS = 320;

/**
 * Cut `text` to at most `max` characters, breaking on a word boundary and
 * marking the cut with an ellipsis. Text at or under the limit is returned
 * unchanged, so the common case allocates nothing surprising.
 */
export function truncate(text: string, max = MAX_CARD_CHARS): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${cut.slice(0, lastSpace > 0 ? lastSpace : max).trimEnd()}…`;
}

/** `"Left kidney"` → `"kidney"`; a name with no side prefix is returned as-is. */
function stripSide(name: string): string {
  const match = /^(?:left|right)\s+(.+)$/i.exec(name.trim());
  return match ? match[1]! : name.trim();
}

function findExplanation(explanations: Readonly<Record<string, string>>, name: string): string | undefined {
  const key = name.trim().toLowerCase();
  const stripped = stripSide(name).toLowerCase();
  for (const candidate of Object.keys(explanations)) {
    const lower = candidate.toLowerCase();
    if (lower === key || lower === stripped) return explanations[candidate];
  }
  return undefined;
}

/** The system a group belongs to: itself if it is one, else its first part's system. */
function systemIdFor(catalog: Catalog, group: Group): string | undefined {
  if (group.kind === "system") return group.id;
  const first = catalog.byId.get(group.parts[0] ?? "");
  return first?.system;
}

/**
 * Resolve the card text for the selected group, honesty rung and all, or
 * `null` when neither rung has anything to say (a system the content does not
 * carry — should not happen with the shipped atlas, but a group with no
 * matching system is not an error, just nothing to show).
 */
export function resolveInfo(content: HumanAtlasText, catalog: Catalog, group: Group): InfoText | null {
  const curated = findExplanation(content.explanations, group.name);
  if (curated !== undefined) return { rung: 1, text: truncate(curated), source: CURATED_SOURCE };

  const systemId = systemIdFor(catalog, group);
  const system = systemId ? content.systems.find((s) => s.id === systemId) : undefined;
  if (system) return { rung: 4, text: truncate(system.description), source: SYSTEM_SOURCE };

  return null;
}
